import { ViewportScroller } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Router, Scroll } from '@angular/router';
import { selectAccountVerificationStatus } from '@mkp/account/state';
import { selectIsNewVersionAvailable } from '@mkp/core/state';
import { isChunkLoadError } from '@mkp/core/util-sentry';
import { SnackbarService } from '@mkp/shared/util-snackbar';
import { createEffect } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, pairwise, switchMap, tap } from 'rxjs/operators';
import { isNavigationEnd, isNavigationError, isNavigationStart } from './router-event.guards';

@Injectable()
export class RouterEffects {
  private readonly router = inject(Router);
  private readonly window = inject(Window);
  private readonly store = inject(Store);
  private readonly snackbarService = inject(SnackbarService);
  private readonly viewportScroller = inject(ViewportScroller);

  readonly listenForNavigationError$ = createEffect(
    () =>
      this.router.events.pipe(
        filter(isNavigationError),
        filter((event) => isChunkLoadError(event.error)),
        tap((event) => this.window.location.replace(event.url))
      ),
    { dispatch: false }
  );

  // on navigation: check the version-store for a new version. If there is one: update the app
  readonly listenForNewVersion$ = createEffect(
    () =>
      this.router.events.pipe(
        filter(isNavigationStart),
        concatLatestFrom(() => this.store.select(selectIsNewVersionAvailable)),
        filter(([, isNewVersionAvailable]) => isNewVersionAvailable),
        tap(([event]) => this.window.location.replace(event.url))
      ),
    { dispatch: false }
  );

  // if user is being verified, we want to display a snackbar when customer service validates their account
  readonly listenForAccountVerification$ = createEffect(
    () =>
      this.router.events.pipe(
        filter(isNavigationEnd),
        switchMap(() => this.store.select(selectAccountVerificationStatus)),
        pairwise(),
        filter(([prev, curr]) => prev.accountId === curr.accountId),
        tap(([prev, curr]) => {
          if (prev.isBeingVerified && !curr.isBeingVerified) {
            this.snackbarService.show('ACCOUNT_VERIFICATION.SUCCESS.MESSSAGE');
          }
        })
      ),
    { dispatch: false }
  );

  // detect navigation events containing a "fragment" option, and scroll to the anchor. For example:
  //  - salary experiment modal will scroll to salary fields, in product-options.effects
  readonly scrollToAnchor$ = createEffect(
    () =>
      this.router.events.pipe(
        filter((e): e is Scroll => e instanceof Scroll),
        tap((e: Scroll) => {
          if (e.anchor) {
            setTimeout(() => {
              this.viewportScroller.scrollToAnchor(e.anchor);
            });
          }
        })
      ),
    { dispatch: false }
  );
}
